/*!
 * UTM / Click-ID capture for Shopify forms (Cocolea)
 * - Saves utm_*, fbclid, gclid from the landing URL (last touch, 90 days)
 * - Fills matching form fields on any page, incl. app forms in open shadow DOM
 * - Hides those fields from visitors
 *
 * Field matching is by label / name / placeholder, so in Shopify Forms just add
 * single-line text fields labelled exactly:
 *   UTM_Source, UTM_Medium, UTM_Campaign, UTM_Content, UTM_Term,
 *   FB_ClickId, Google_ClickId, Form_Id
 * (Do NOT mark them as required.)
 */
(function () {
  'use strict';

  var STORE_KEY = 'cocolea_attribution';
  var TTL_MS = 90 * 24 * 60 * 60 * 1000; // keep values for 90 days
  var TRACKED = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid'];

  // normalised field label/name  ->  data key
  var FIELD_MAP = {
    utmsource: 'utm_source',
    utmmedium: 'utm_medium',
    utmcampaign: 'utm_campaign',
    utmcontent: 'utm_content',
    utmterm: 'utm_term',
    fbclickid: 'fbclid',
    fbclid: 'fbclid',
    googleclickid: 'gclid',
    gclid: 'gclid',
    formid: 'formid'
  };

  // Optional: fixed Form_Id per page path. Anything not listed falls back to
  // ?formid= in the URL, then the form's own id, then "<page-slug>-form-<n>".
  var FORM_IDS = {
    // '/pages/contact': 'cocolea-contact',
    // '/': 'cocolea-home-enquiry'
  };

  /* ---------- storage ---------- */
  function load() {
    try {
      var d = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
      if (d && Date.now() - d._ts < TTL_MS) return d;
    } catch (e) {}
    return {};
  }
  function save(d) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(d)); } catch (e) {}
  }

  /* ---------- 1. capture from URL ---------- */
  var params = new URLSearchParams(window.location.search);
  var fresh = {}, hasNew = false;
  TRACKED.forEach(function (k) {
    var v = params.get(k);
    if (v) { fresh[k] = v; hasNew = true; }
  });
  // New campaign visit replaces the whole set so values never mix across campaigns
  if (hasNew) { fresh._ts = Date.now(); save(fresh); }
  var data = hasNew ? fresh : load();

  /* ---------- helpers ---------- */
  function norm(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }

  function labelText(input) {
    var root = input.getRootNode ? input.getRootNode() : document;
    var txt = '';
    if (input.id && root.querySelector) {
      try {
        var l = root.querySelector('label[for="' + CSS.escape(input.id) + '"]');
        if (l) txt += ' ' + l.textContent;
      } catch (e) {}
    }
    var wrap = input.closest && input.closest('label');
    if (wrap) txt += ' ' + wrap.textContent;
    var by = input.getAttribute('aria-labelledby');
    if (by && root.getElementById) {
      by.split(/\s+/).forEach(function (id) {
        var el = root.getElementById(id);
        if (el) txt += ' ' + el.textContent;
      });
    }
    return txt;
  }

  function matchKey(input) {
    var candidates = [
      input.name, input.id, input.placeholder,
      input.getAttribute('aria-label'), labelText(input)
    ];
    for (var i = 0; i < candidates.length; i++) {
      var c = norm(candidates[i]);
      if (!c) continue;
      for (var mk in FIELD_MAP) {
        // exact, or ends with (e.g. contact[UTM_Source] -> contactutmsource)
        if (c === mk || c.slice(-mk.length) === mk) return FIELD_MAP[mk];
      }
    }
    return null;
  }

  function formIdFor(input) {
    if (params.get('formid')) return params.get('formid');
    if (FORM_IDS[location.pathname]) return FORM_IDS[location.pathname];
    var form = input.closest && input.closest('form');
    if (form && form.getAttribute('data-form-id')) return form.getAttribute('data-form-id');
    if (form && form.id) return form.id;
    var slug = location.pathname.replace(/^\/|\/$/g, '').replace(/\//g, '-') || 'home';
    var all = Array.prototype.slice.call(document.querySelectorAll('form'));
    var idx = form ? all.indexOf(form) + 1 : 1;
    return slug + '-form-' + (idx > 0 ? idx : 1);
  }

  // Set value in a way framework-rendered inputs (React/Preact) also register
  function setValue(input, value) {
    var proto = input.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    var setter = Object.getOwnPropertyDescriptor(proto, 'value').set;
    setter.call(input, value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Hide the field's wrapper (label + input), not the whole form
  function hideField(input) {
    if (input.type === 'hidden') return;
    var el = input;
    while (el.parentElement &&
           el.parentElement.querySelectorAll('input, textarea, select').length === 1 &&
           el.parentElement.tagName !== 'FORM') {
      el = el.parentElement;
    }
    el.style.setProperty('display', 'none', 'important');
    input.setAttribute('tabindex', '-1');
    input.setAttribute('aria-hidden', 'true');
  }

  /* ---------- 2. fill fields ---------- */
  function processInput(input) {
    if (input.dataset.utmDone) return;
    var key = matchKey(input);
    if (!key) return;
    var value = key === 'formid' ? formIdFor(input) : (data[key] || '');
    if (value) setValue(input, value);
    hideField(input);
    input.dataset.utmDone = '1';
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('input, textarea').forEach(processInput);
    // look inside open shadow roots (app-rendered forms)
    root.querySelectorAll('*').forEach(function (el) {
      if (el.shadowRoot) scan(el.shadowRoot);
    });
  }

  var pending = false;
  function scheduleScan() {
    if (pending) return;
    pending = true;
    setTimeout(function () { pending = false; scan(document); }, 250);
  }

  function start() {
    scan(document);
    // forms from apps render late, so watch for them
    new MutationObserver(scheduleScan).observe(document.documentElement, { childList: true, subtree: true });
    // shadow-DOM changes aren't seen by the observer, so re-check for a while
    var n = 0, t = setInterval(function () { scan(document); if (++n >= 20) clearInterval(t); }, 1000);
    // and whenever someone starts filling a form (focusin crosses shadow DOM)
    document.addEventListener('focusin', scheduleScan, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();